import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Download, Upload, ScanFace, KeyRound, Lock, Trash2, SunMoon } from "lucide-react";
import { toast } from "sonner";
import * as vault from "../lib/vault";
import { backupFilename, readJsonFile, saveJsonFile } from "../lib/backupFile";
import { getThemeChoice, setThemeChoice } from "../lib/theme";
import { faceIdErrorMessage } from "../components/LockShell";
import PinPad from "../components/PinPad";
import Sheet from "../components/Sheet";

const THEME_OPTIONS = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

function ThemePicker() {
  const [choice, setChoice] = useState(getThemeChoice);
  const pick = (value) => {
    setChoice(value);
    setThemeChoice(value);
  };
  return (
    <div className="grid grid-cols-3 gap-1 p-1 rounded-full bg-nest-surface-2 border border-nest-line" role="radiogroup">
      {THEME_OPTIONS.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={choice === o.value}
          onClick={() => pick(o.value)}
          data-testid={`theme-${o.value}`}
          className={`py-2 rounded-full text-sm transition-colors ${
            choice === o.value ? "nest-brand-fill text-white shadow-sm" : "text-nest-soft"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="nest-card p-5 md:p-6">
    <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Row = ({ icon: Icon, title, sub, children, testId }) => (
  <div className="flex items-center justify-between gap-4" data-testid={testId}>
    <div className="flex items-start gap-3 min-w-0">
      <Icon size={18} strokeWidth={1.5} className="mt-0.5 text-nest-brand shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-xs text-nest-muted mt-0.5">{sub}</div>}
      </div>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const smallBtn =
  "text-sm px-4 py-2 rounded-full border border-nest-line bg-nest-surface-3 hover:bg-nest-surface-5 disabled:opacity-50";

export function daysSince(ts) {
  return ts ? Math.floor((Date.now() - ts) / 86400000) : null;
}

function ChangePin({ onClose }) {
  const [step, setStep] = useState("current");
  const [newPin, setNewPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const retry = (msg, backTo) => {
    setError(msg);
    setResetKey((k) => k + 1);
    setStep(backTo);
  };

  const onComplete = async (pin) => {
    setError("");
    if (step === "current") {
      setBusy(true);
      const ok = await vault.verifyPin(pin);
      setBusy(false);
      return ok ? setStep("new") : retry("Wrong PIN", "current");
    }
    if (step === "new") {
      setNewPin(pin);
      return setStep("confirm");
    }
    if (pin !== newPin) return retry("PINs didn't match — try again", "new");
    setBusy(true);
    await vault.changePin(pin);
    toast.success("PIN changed");
    onClose();
  };

  const titles = { current: "Enter current PIN", new: "Choose a new PIN", confirm: "Confirm new PIN" };
  return (
    <Sheet title={titles[step]} onClose={onClose} testId="change-pin-sheet">
      <div className="py-2">
        <PinPad key={step} onComplete={onComplete} busy={busy} error={error} resetKey={resetKey} />
      </div>
    </Sheet>
  );
}

export default function Settings() {
  const state = useSyncExternalStore(vault.subscribe, vault.getState);
  const [faceIdOk, setFaceIdOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [changingPin, setChangingPin] = useState(false);
  const fileRef = useRef(null);
  const faceIdOn = vault.hasFaceId();

  useEffect(() => {
    vault.faceIdAvailable().then(setFaceIdOk);
  }, []);

  const backup = async () => {
    // Open the share sheet straight from the tap (iOS requires it), then record the date.
    const result = await saveJsonFile(vault.exportBackup(), backupFilename());
    if (result !== "cancelled") {
      await vault.markBackedUp();
      toast.success("Backup created");
    }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const json = await readJsonFile(file);
      const data = vault.parseBackup(json);
      const ok = window.confirm(
        `Replace everything in Nest with this backup?\n\n` +
          `It has ${data.expenses.length} expenses, ${data.debts.length} debt snapshots, ` +
          `${data.wealth.length} wealth snapshots and ${data.accounts.length} accounts.`
      );
      if (!ok) return;
      await vault.restoreBackup(json);
      toast.success("Backup restored");
    } catch {
      toast.error("That file isn't a Nest backup");
    }
  };

  const toggleFaceId = async () => {
    setBusy(true);
    try {
      if (faceIdOn) {
        await vault.disableFaceId();
        toast.success("Face ID turned off");
      } else {
        await vault.enableFaceId();
        toast.success("Face ID turned on");
      }
    } catch (err) {
      toast.error(faceIdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const erase = async () => {
    const answer = window.prompt(
      "This permanently deletes all Nest data on this phone. Make sure you have a backup.\n\nType ERASE to confirm."
    );
    if (answer === "ERASE") await vault.eraseEverything();
  };

  const backupAge = daysSince(state?.lastBackupAt);

  return (
    <div className="space-y-6 md:space-y-8" data-testid="settings-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-nest-muted">Nest</div>
        <h1 className="font-heading text-3xl md:text-5xl font-medium mt-2">Settings</h1>
      </header>

      <Section title="Appearance">
        <Row icon={SunMoon} title="Theme" sub="System follows your phone's light / dark setting" />
        <ThemePicker />
      </Section>

      <Section title="Backups">
        <p className="text-xs text-nest-muted -mt-1">
          Your data only lives on this phone. Back up regularly and save the file to iCloud Drive
          ("Save to Files"), so you can restore it if the app is deleted or you get a new phone.
        </p>
        <Row
          icon={Download}
          title="Back up now"
          sub={
            backupAge == null
              ? "No backup made yet"
              : `Last backup ${backupAge === 0 ? "today" : `${backupAge} day${backupAge > 1 ? "s" : ""} ago`}`
          }
          testId="backup-row"
        >
          <button onClick={backup} className={smallBtn} data-testid="backup-btn">
            Back up
          </button>
        </Row>
        <Row icon={Upload} title="Restore from backup" sub="Replaces all current data">
          <button onClick={() => fileRef.current?.click()} className={smallBtn} data-testid="restore-btn">
            Restore
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={restore}
            data-testid="settings-restore-input"
          />
        </Row>
      </Section>

      <Section title="Security">
        <Row
          icon={ScanFace}
          title="Face ID"
          sub={
            faceIdOk
              ? faceIdOn
                ? "On — unlock with your face"
                : "Off — unlock with your PIN only"
              : "Not available in this browser"
          }
        >
          <button
            onClick={toggleFaceId}
            disabled={busy || (!faceIdOk && !faceIdOn)}
            className={smallBtn}
            data-testid="faceid-toggle-btn"
          >
            {faceIdOn ? "Turn off" : "Turn on"}
          </button>
        </Row>
        <Row icon={KeyRound} title="Change PIN">
          <button onClick={() => setChangingPin(true)} className={smallBtn} data-testid="change-pin-btn">
            Change
          </button>
        </Row>
        <Row icon={Lock} title="Lock now" sub="Nest also locks itself after a minute in the background">
          <button onClick={vault.lock} className={smallBtn}>
            Lock
          </button>
        </Row>
      </Section>

      <Section title="Danger zone">
        <Row icon={Trash2} title="Erase all data on this phone" sub="Can't be undone without a backup">
          <button onClick={erase} className={`${smallBtn} text-nest-warn`} data-testid="erase-btn">
            Erase
          </button>
        </Row>
      </Section>

      {changingPin && <ChangePin onClose={() => setChangingPin(false)} />}
    </div>
  );
}
